import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp, ArrowUpCircle, Gem,
  Wallet, IndianRupee, ArrowDownLeft, BadgePercent, CheckCircle2,
} from 'lucide-react';
import { dashboardAPI } from '../api/dashboard';
import { bannersAPI, resolveBannerUrl } from '../api/banners';
import { useAuth } from '../context/AuthContext';
import { GoldCard, Card } from '../components/ui/Card';
import StatusBadge from '../components/ui/StatusBadge';

// ── helpers ──────────────────────────────────────────────────
const fmt  = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
const fmtK = (n) => {
  const num = Number(n || 0);
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
  if (num >= 1000)   return `₹${(num / 1000).toFixed(1)}K`;
  return `₹${num.toFixed(2)}`;
};

const ACTIVITY_LABEL = { deposit: 'Deposit', profit: 'Profit Interest' };

const container = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const item      = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.35 } } };

// ── stat pill under the gold card ────────────────────────────
function StatPill({ label, value, dimmed }) {
  return (
    <div>
      <p className="text-[11px] mb-0.5" style={{ color: dimmed ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.65)' }}>
        {label}
      </p>
      <p className="text-sm font-bold text-white">{value}</p>
    </div>
  );
}

// ── activity icon ─────────────────────────────────────────────
function ActivityIcon({ type }) {
  if (type === 'profit') {
    return (
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
           style={{ background: 'rgba(99,102,241,0.18)' }}>
        <BadgePercent size={18} color="#a5b4fc" />
      </div>
    );
  }
  return (
    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
         style={{ background: 'rgba(212,160,23,0.15)' }}>
      <IndianRupee size={18} color="#f0c040" />
    </div>
  );
}

// ── skeleton cards ────────────────────────────────────────────
function BalanceSkeleton() {
  return <div className="skeleton h-44 rounded-3xl" />;
}

function StatsSkeleton() {
  return (
    <div className="grid grid-cols-3 gap-3">
      {[1, 2, 3].map((i) => <div key={i} className="skeleton h-20 rounded-2xl" />)}
    </div>
  );
}

// ── Banner Slideshow ──────────────────────────────────────────
function BannerSlideshow() {
  const [banners,  setBanners]  = useState([]);
  const [current,  setCurrent]  = useState(0);
  const timerRef               = useRef(null);

  useEffect(() => {
    bannersAPI.get()
      .then(({ data }) => { if (data.status) setBanners(data.data.banners ?? []); })
      .catch(() => {});
  }, []);

  const resetTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent((c) => (c + 1) % banners.length);
    }, 3500);
  };

  useEffect(() => {
    if (banners.length < 2) return;
    resetTimer();
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banners]);

  const goTo = (idx) => {
    setCurrent(idx);
    resetTimer();
  };

  if (!banners.length) return null;

  return (
    <div>
      <h2 className="text-base font-serif font-semibold text-white mb-3">Promotions</h2>
      <div className="relative overflow-hidden rounded-2xl" style={{ aspectRatio: '16/7' }}>
        <AnimatePresence mode="wait">
          <motion.img
            key={banners[current].id}
            src={resolveBannerUrl(banners[current].image_url)}
            alt={banners[current].title}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.35 }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>

        {/* Title overlay */}
        {banners[current].title && (
          <div
            className="absolute bottom-0 left-0 right-0 px-4 py-3"
            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, transparent 100%)' }}
          >
            <p className="text-sm font-semibold text-white truncate">
              {banners[current].title}
            </p>
          </div>
        )}

        {/* Dot indicators */}
        {banners.length > 1 && (
          <div className="absolute bottom-2.5 right-3 flex gap-1.5">
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className="rounded-full transition-all"
                style={{
                  width:   i === current ? 18 : 6,
                  height:  6,
                  background: i === current ? '#f0c040' : 'rgba(255,255,255,0.45)',
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Gold purchase preference ───────────────────────────────────
const GOLD_OPTIONS = [8, 16, 24, 32, 40, 48, 56, 64, 72, 80];

function GoldPurchasePreference({ selected, onSelect }) {
  const [saving,  setSaving]  = useState(false);
  const [flash,   setFlash]   = useState(null); // 'ok' | 'err'

  const handleSelect = async (grams) => {
    if (saving || grams === selected) return;
    setSaving(true);
    setFlash(null);
    try {
      await dashboardAPI.saveGoldPreference(grams);
      onSelect(grams);
      setFlash('ok');
    } catch {
      setFlash('err');
    } finally {
      setSaving(false);
      setTimeout(() => setFlash(null), 2500);
    }
  };

  return (
    <Card className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div
              className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(212,160,23,0.15)' }}
            >
              <Gem size={13} color="#d4a017" />
            </div>
            <p className="text-xs font-semibold uppercase tracking-widest"
               style={{ color: 'rgba(212,160,23,0.8)' }}>
              Gold Goal
            </p>
          </div>
          <p className="text-sm font-semibold text-white leading-snug">
            How much gold do you want to purchase?
          </p>
        </div>

        {/* Flash feedback */}
        <AnimatePresence>
          {flash === 'ok' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-1 flex-shrink-0"
            >
              <CheckCircle2 size={14} color="#6ee7b7" />
              <span className="text-xs font-medium" style={{ color: '#6ee7b7' }}>Saved</span>
            </motion.div>
          )}
          {flash === 'err' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
            >
              <span className="text-xs font-medium" style={{ color: '#fca5a5' }}>Failed</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Option grid */}
      <div className="grid grid-cols-5 gap-2">
        {GOLD_OPTIONS.map((g) => {
          const isActive = g === selected;
          return (
            <motion.button
              key={g}
              type="button"
              disabled={saving}
              whileTap={{ scale: 0.92 }}
              onClick={() => handleSelect(g)}
              className="rounded-2xl py-2.5 text-center transition-all disabled:opacity-60"
              style={
                isActive
                  ? {
                      background: 'linear-gradient(135deg, #d4a017, #f0c040)',
                      color: '#fff',
                      boxShadow: '0 2px 10px rgba(212,160,23,0.4)',
                    }
                  : {
                      background: 'rgba(212,160,23,0.08)',
                      color: '#f0c040',
                      border: '1px solid rgba(212,160,23,0.2)',
                    }
              }
            >
              <p className="text-sm font-bold leading-none">{g}</p>
              <p className={`text-[10px] mt-0.5 ${isActive ? 'text-amber-100' : ''}`}
                 style={!isActive ? { color: 'rgba(240,192,64,0.55)' } : {}}>
                g
              </p>
            </motion.button>
          );
        })}
      </div>

      {selected && (
        <p className="text-xs text-center" style={{ color: 'rgba(255,255,255,0.4)' }}>
          Your goal: <span style={{ color: '#f0c040' }} className="font-semibold">{selected}g of gold</span>
        </p>
      )}
    </Card>
  );
}

// ── main page ─────────────────────────────────────────────────
export default function DashboardPage() {
  const { user } = useAuth();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [goldPref, setGoldPref] = useState(null);

  useEffect(() => {
    dashboardAPI.getSummary()
      .then((r) => {
        setData(r.data.data);
        setGoldPref(r.data.data?.gold_preference_grams ?? null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const hasWithdrawn = parseFloat(data?.total_withdrawn || 0) > 0;

  return (
    <div className="min-h-screen">

      {/* ── Greeting ── */}
      <div className="px-5 pt-14 pb-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>Good day,</p>
            <h1 className="text-2xl font-serif font-bold text-white">
              {user?.name?.split(' ')[0]} 👋
            </h1>
          </div>
          {data?.gold_rate?.rate && (
            <div
              className="flex items-center gap-1.5 rounded-2xl px-3 py-2"
              style={{ background: 'rgba(212,160,23,0.12)', border: '1px solid rgba(212,160,23,0.25)' }}
            >
              <Gem size={14} color="#d4a017" />
              <div>
                <p className="text-[9px] uppercase tracking-widest" style={{ color: 'rgba(212,160,23,0.7)' }}>Gold Rate</p>
                <p className="text-xs font-bold" style={{ color: '#f0c040' }}>
                  ₹{Number(data.gold_rate.rate).toLocaleString('en-IN')}/g
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="px-4 pb-6 flex flex-col gap-4">

        {/* ── Balance card ── */}
        {loading ? <BalanceSkeleton /> : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <GoldCard className="rounded-3xl">
              <div className="flex items-center gap-2 mb-1">
                <Wallet size={14} color="rgba(255,255,255,0.7)" />
                <p className="text-amber-100 text-xs font-medium uppercase tracking-widest">
                  Available Balance
                </p>
              </div>
              <p className="text-4xl font-bold text-white mb-5 leading-tight">
                ₹{fmt(data?.total_balance)}
              </p>

              {/* Breakdown pills */}
              <div className="flex gap-5 border-t pt-4"
                   style={{ borderColor: 'rgba(255,255,255,0.2)' }}>
                <StatPill label="Deposited"  value={fmtK(data?.deposited_amount)} />
                <StatPill label="Profit"     value={fmtK(data?.total_profit)} />
                {hasWithdrawn && (
                  <StatPill label="Withdrawn" value={fmtK(data?.total_withdrawn)} dimmed />
                )}
              </div>
            </GoldCard>
          </motion.div>
        )}

        {/* ── Stat cards ── */}
        {loading ? <StatsSkeleton /> : (
          <motion.div
            variants={container} initial="hidden" animate="show"
            className="grid grid-cols-3 gap-3"
          >
            {[
              {
                label: 'Deposited',
                value: fmtK(data?.deposited_amount),
                full:  `₹${fmt(data?.deposited_amount)}`,
                bg:    'rgba(212,160,23,0.12)',
                color: '#f0c040',
                Icon:  IndianRupee,
              },
              {
                label: 'Profit',
                value: fmtK(data?.total_profit),
                full:  `₹${fmt(data?.total_profit)}`,
                bg:    'rgba(99,102,241,0.15)',
                color: '#a5b4fc',
                Icon:  BadgePercent,
              },
              {
                label: 'Withdrawn',
                value: fmtK(data?.total_withdrawn),
                full:  `₹${fmt(data?.total_withdrawn)}`,
                bg:    'rgba(5,150,105,0.15)',
                color: '#6ee7b7',
                Icon:  ArrowDownLeft,
              },
            ].map(({ label, value, full, bg, color, Icon }) => (
              <motion.div key={label} variants={item}>
                <Card className="flex flex-col gap-2 py-3.5 px-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl"
                       style={{ background: bg }}>
                    <Icon size={15} color={color} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider mb-0.5"
                       style={{ color: 'rgba(255,255,255,0.4)' }}>{label}</p>
                    <p className="text-sm font-bold text-white leading-tight">{value}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* ── Gold purchase preference ── */}
        {!loading && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <GoldPurchasePreference
              selected={goldPref}
              onSelect={setGoldPref}
            />
          </motion.div>
        )}

        {/* ── Quick actions ── */}
        <motion.div
          variants={container} initial="hidden" animate="show"
          className="grid grid-cols-2 gap-3"
        >
          <motion.div variants={item}>
            <Link to="/invest">
              <Card className="flex items-center gap-3 cursor-pointer">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                     style={{ background: 'rgba(212,160,23,0.15)' }}>
                  <TrendingUp size={20} color="#f0c040" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Invest</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>Grow wealth</p>
                </div>
              </Card>
            </Link>
          </motion.div>

          <motion.div variants={item}>
            <Link to="/withdraw">
              <Card className="flex items-center gap-3 cursor-pointer">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                     style={{ background: 'rgba(5,150,105,0.18)' }}>
                  <ArrowUpCircle size={20} color="#6ee7b7" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Withdraw</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>Transfer funds</p>
                </div>
              </Card>
            </Link>
          </motion.div>
        </motion.div>

        {/* ── Recent activity ── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-serif font-semibold text-white">Recent Activity</h2>
            <Link to="/transactions" className="text-xs font-medium" style={{ color: '#f0c040' }}>
              View all
            </Link>
          </div>

          {loading ? (
            <div className="flex flex-col gap-3">
              {[1, 2, 3].map((i) => <div key={i} className="skeleton h-16 rounded-2xl" />)}
            </div>
          ) : data?.recent_activity?.length ? (
            <div className="flex flex-col gap-3">
              {data.recent_activity.map((act, idx) => (
                <motion.div
                  key={act.id || idx}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.06 }}
                >
                  <Card className="flex items-center gap-3">
                    <ActivityIcon type={act.type} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white">
                        {ACTIVITY_LABEL[act.type] ?? act.type}
                      </p>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                        {act.note
                          ? act.note.length > 28 ? act.note.slice(0, 28) + '…' : act.note
                          : new Date(act.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-white">₹{fmt(act.amount)}</p>
                      <StatusBadge status={act.status} />
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <Card className="text-center py-8">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3"
                   style={{ background: 'rgba(212,160,23,0.15)' }}>
                <TrendingUp size={24} color="#f0c040" />
              </div>
              <p className="text-sm font-medium text-white">No activity yet</p>
              <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Start your wealth journey today
              </p>
              <Link to="/invest" className="inline-block mt-4 btn-gold text-sm px-5 py-2.5 rounded-xl" style={{ borderRadius: '12px' }}>
                Explore Plans
              </Link>
            </Card>
          )}
        </div>

        {/* ── Banner Slideshow ── */}
        <BannerSlideshow />

      </div>
    </div>
  );
}
